import { createElement, type HTMLAttributes, type ReactNode } from 'react';
import DOMPurify from 'dompurify';

import { MathText } from '@/features/content/MathText';

import {
  CHAT_ALLOWED_ATTRS,
  CHAT_ALLOWED_TAGS,
  filterClassNames,
  isAllowedImageSrc,
  isAllowedLinkHref,
} from './chatHtmlPolicy';
import styles from './ChatHtmlRenderer.module.css';

interface ChatHtmlRendererProps {
  // Exact URLs of this chapter's figures. An <img> matching one of these is
  // allowed; everything else must be on the external host allowlist.
  chapterImageUrls?: ReadonlySet<string>;
  // Drops the readable-measure cap on prose. Chat needs it because its bubble
  // can be far wider than a comfortable line; a container that is already the
  // right width does not, and keeping it there wraps text short of the edge.
  fullWidth?: boolean;
  html: string;
}

// Elements that cannot take children. `createElement` throws if you pass any.
const VOID_TAGS = new Set(['br', 'hr', 'img']);

// Tables are wrapped so wide content scrolls inside the panel instead of
// pushing the page sideways. This is the single most important layout rule for
// rich replies in a narrow column.
const SCROLL_WRAPPED_TAGS = new Set(['table', 'pre']);

function getProps(
  tagName: string,
  element: Element,
  key: string,
  chapterImageUrls: ReadonlySet<string>,
): HTMLAttributes<HTMLElement> & { key: string } {
  const props: Record<string, unknown> = { key };
  const classNames: string[] = [];

  for (const attribute of Array.from(element.attributes)) {
    if (attribute.name === 'class') {
      classNames.push(...filterClassNames(attribute.value));
      continue;
    }

    // Re-checked here even though DOMPurify already ran: this is the boundary,
    // and the policy is deliberately narrower than "any URL".
    if (attribute.name === 'href') {
      if (isAllowedLinkHref(attribute.value)) {
        props.href = attribute.value;
      }
      continue;
    }

    if (attribute.name === 'src') {
      if (isAllowedImageSrc(attribute.value, chapterImageUrls)) {
        props.src = attribute.value;
      }
      continue;
    }

    if (attribute.name === 'colspan') {
      props.colSpan = Number(attribute.value) || undefined;
      continue;
    }

    if (attribute.name === 'rowspan') {
      props.rowSpan = Number(attribute.value) || undefined;
      continue;
    }

    props[attribute.name] = attribute.value;
  }

  if (classNames.length > 0) {
    props.className = Array.from(new Set(classNames)).join(' ');
  }

  if (tagName === 'a') {
    props.rel = 'noreferrer noopener';
    props.target = '_blank';
  }

  if (tagName === 'img') {
    props.loading = 'lazy';
    props.className = [props.className, styles.image].filter(Boolean).join(' ');
  }

  return props as unknown as HTMLAttributes<HTMLElement> & { key: string };
}

function renderNode(
  node: ChildNode,
  key: string,
  chapterImageUrls: ReadonlySet<string>,
): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    // Math travels as $…$ / $$…$$ inside text, consistent with the rest of
    // Kudu's content rendering, so KaTeX handles it the same way everywhere.
    return <MathText key={key} text={node.textContent ?? ''} />;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as Element;
  const tagName = element.tagName.toLowerCase();
  const props = getProps(tagName, element, key, chapterImageUrls);

  // An <img> whose src did not survive the allowlist would render as a broken
  // image, so drop the element entirely.
  if (tagName === 'img' && !('src' in props)) {
    return null;
  }

  if (VOID_TAGS.has(tagName)) {
    return createElement(tagName, props);
  }

  const children = Array.from(element.childNodes).map((child, index) =>
    renderNode(child, `${key}-${index}`, chapterImageUrls),
  );
  const rendered = createElement(tagName, props, children);

  if (SCROLL_WRAPPED_TAGS.has(tagName)) {
    return createElement('div', { className: styles.scroller, key: `${key}-scroll` }, rendered);
  }

  return rendered;
}

export function ChatHtmlRenderer({
  chapterImageUrls = new Set<string>(),
  fullWidth = false,
  html,
}: ChatHtmlRendererProps) {
  const rendererClass = fullWidth
    ? `${styles.renderer} ${styles.fullWidth}`
    : styles.renderer;
  // Explicit allowlists rather than DOMPurify's permissive defaults. Malformed
  // markup is parsed and repaired rather than throwing, so a bad model response
  // degrades into readable content instead of blanking the conversation.
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_ATTR: CHAT_ALLOWED_ATTRS,
    ALLOWED_TAGS: CHAT_ALLOWED_TAGS,
    FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form', 'input'],
  });

  if (typeof DOMParser === 'undefined') {
    return (
      <div className={rendererClass}>
        <MathText text={sanitized} />
      </div>
    );
  }

  const parsed = new DOMParser().parseFromString(sanitized, 'text/html');

  return (
    <div className={rendererClass}>
      {Array.from(parsed.body.childNodes).map((node, index) =>
        renderNode(node, `chat-${index}`, chapterImageUrls),
      )}
    </div>
  );
}
